(() => {
  'use strict';
  const KEY = 'BosscoAccessoryDetails';
  if (window[KEY]) {
    window[KEY].scan();
    return;
  }

  const ROOT = '[data-bc-accessory-details]';
  const GALLERY = '[data-bc-details-gallery]';
  const MODEL = '[data-bc-accessory-model] model-viewer';
  const STYLE = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
  const viewers = new Map();
  const galleries = new Map();
  const unloading = new WeakSet();
  let loading = false;
  let failed = false;
  let timer = 0;
  let retries = 0;

  function rootsWithin(target) {
    const roots = Array.from(target.querySelectorAll?.(ROOT) || []);
    if (target.matches?.(ROOT)) roots.unshift(target);
    return roots;
  }

  function ensureStyles() {
    if (document.querySelector(`link[href="${STYLE}"]`)) return;
    if (window.theme?.loadStyleOnce) {
      window.theme.loadStyleOnce(STYLE);
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = STYLE;
    document.head.append(link);
  }

  function destroy(model) {
    const viewer = viewers.get(model);
    if (!viewer) return;
    viewers.delete(model);
    viewer.destroy();
  }

  function setupGallery(gallery) {
    const track = gallery.querySelector('[data-bc-details-slides]');
    const slides = Array.from(track?.querySelectorAll('[data-bc-details-slide]') || []);
    if (!track || slides.length < 2) return null;
    const thumbs = Array.from(gallery.querySelectorAll('[data-bc-details-thumb]'));
    const previous = gallery.querySelector('[data-bc-details-prev]');
    const next = gallery.querySelector('[data-bc-details-next]');
    const counter = gallery.querySelector('[data-bc-details-current]');
    const listeners = [];
    let current = 0;
    let requested = null;
    let frame = 0;
    const behavior = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const listen = (node, name, callback, options) => {
      if (!node) return;
      node.addEventListener(name, callback, options);
      listeners.push(() => node.removeEventListener(name, callback, options));
    };

    function select(index) {
      current = Math.max(0, Math.min(slides.length - 1, index));
      if (counter && counter.textContent !== String(current + 1)) counter.textContent = String(current + 1);
      if (previous) previous.disabled = current === 0;
      if (next) next.disabled = current === slides.length - 1;
      let active;
      for (const thumb of thumbs) {
        if (Number(thumb.dataset.bcDetailsThumb) === current) {
          thumb.setAttribute('aria-current', 'true');
          active = thumb;
        } else thumb.removeAttribute('aria-current');
      }
      // Scroll only the thumbnail strip, never the page or an ancestor panel.
      const strip = active?.closest('.bc-accessory-details__thumbs');
      if (!strip || strip.scrollWidth <= strip.clientWidth) return;
      const bounds = strip.getBoundingClientRect();
      const image = active.getBoundingClientRect();
      const delta = image.left < bounds.left ? image.left - bounds.left : image.right > bounds.right ? image.right - bounds.right : 0;
      if (delta) strip.scrollTo({ left: strip.scrollLeft + delta, behavior: 'auto' });
    }

    function goTo(index, motion = behavior()) {
      select(index);
      requested = current;
      const left = track.scrollLeft + slides[current].getBoundingClientRect().left - track.getBoundingClientRect().left - track.clientLeft;
      track.scrollTo({ left, behavior: motion });
    }

    function selectVariantImage(imageId) {
      if (!imageId) return;
      const index = slides.findIndex(slide => slide.dataset.bcDetailsImageId === String(imageId));
      if (index >= 0) goTo(index, 'auto');
    }

    function updateFromScroll() {
      frame = 0;
      const bounds = track.getBoundingClientRect();
      if (!bounds.width) return;
      const center = bounds.left + bounds.width / 2;
      let nearest = 0;
      let distance = Infinity;
      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) { distance = delta; nearest = index; }
      });
      select(nearest);
      if (nearest === requested) requested = null;
    }

    listen(track, 'scroll', () => {
      if (!frame) frame = window.requestAnimationFrame(updateFromScroll);
    }, { passive: true });
    const clearRequest = () => { requested = null; };
    listen(track, 'pointerdown', clearRequest, { passive: true });
    listen(track, 'touchstart', clearRequest, { passive: true });
    listen(track, 'wheel', clearRequest, { passive: true });
    listen(track, 'scrollend', clearRequest);
    listen(track, 'keydown', event => {
      if (event.target !== track || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      goTo((requested ?? current) + (event.key === 'ArrowRight' ? 1 : -1));
    });
    for (const thumb of thumbs) {
      listen(thumb, 'click', event => {
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const index = Number(thumb.dataset.bcDetailsThumb);
        if (!Number.isInteger(index) || index < 0 || index >= slides.length) return;
        event.preventDefault();
        goTo(index);
      });
    }
    listen(previous, 'click', () => goTo((requested ?? current) - 1));
    listen(next, 'click', () => goTo((requested ?? current) + 1));
    const purchase = gallery.closest('[data-bc-accessory-landing]')?.querySelector('[data-bc-purchase]');
    listen(purchase, 'bc:accessory:variant-change', event => selectVariantImage(event.detail?.imageId));
    const resize = () => goTo(requested ?? current, 'auto');
    let observer;
    if (window.ResizeObserver) {
      observer = new window.ResizeObserver(resize);
      observer.observe(track);
    } else listen(window, 'resize', resize);
    if (previous) previous.hidden = false;
    if (next) next.hidden = false;
    updateFromScroll();
    const initialImage = purchase?.dataset.bcGalleryVariant
      ? purchase.dataset.bcGalleryImage
      : gallery.dataset.bcInitialImageId;
    selectVariantImage(initialImage);
    return () => {
      listeners.forEach(remove => remove());
      if (frame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      if (previous) previous.hidden = true;
      if (next) next.hidden = true;
    };
  }

  function scanGalleries() {
    for (const [gallery, cleanup] of galleries) {
      const root = gallery.closest(ROOT);
      if (!gallery.isConnected || !root || unloading.has(root)) {
        cleanup();
        galleries.delete(gallery);
      }
    }
    for (const gallery of document.querySelectorAll(GALLERY)) {
      if (!gallery.matches?.(GALLERY) || galleries.has(gallery)) continue;
      const root = gallery.closest(ROOT);
      if (!root || unloading.has(root)) continue;
      const cleanup = setupGallery(gallery);
      if (cleanup) galleries.set(gallery, cleanup);
    }
  }

  function scan() {
    scanGalleries();
    for (const model of viewers.keys()) {
      const root = model.closest(ROOT);
      if (!model.isConnected || !root || unloading.has(root)) destroy(model);
    }
    const models = Array.from(document.querySelectorAll(MODEL)).filter(model => {
      const root = model.closest(ROOT);
      return root && model.isConnected && !unloading.has(root) && !viewers.has(model);
    });
    if (!models.length) return;
    ensureStyles();

    if (typeof window.Shopify?.ModelViewerUI === 'function') {
      for (const model of models) viewers.set(model, new window.Shopify.ModelViewerUI(model));
      return;
    }
    if (loading || failed) return;
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      if (!timer && retries < 50) {
        retries++;
        timer = setTimeout(() => {
          timer = 0;
          scan();
        }, 100);
      }
      return;
    }

    loading = true;
    window.Shopify.loadFeatures([{
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad(error) {
        loading = false;
        failed = !!error || typeof window.Shopify?.ModelViewerUI !== 'function';
        if (!failed) scan();
      }
    }]);
  }

  document.addEventListener('shopify:section:unload', event => {
    for (const root of rootsWithin(event.target)) unloading.add(root);
    for (const [gallery, cleanup] of galleries) {
      if (event.target.contains(gallery)) {
        cleanup();
        galleries.delete(gallery);
      }
    }
    for (const model of viewers.keys()) {
      if (event.target.contains(model)) destroy(model);
    }
  });
  document.addEventListener('shopify:section:load', event => {
    for (const root of rootsWithin(event.target)) unloading.delete(root);
    retries = 0;
    failed = false;
    scan();
  });
  document.addEventListener('DOMContentLoaded', scan, { once: true });
  window.addEventListener('load', scan, { once: true });
  window[KEY] = { scan };
  scan();
})();
